import { useMemo } from 'react'
import { createClient, Provider as GQLProvider, fetchExchange } from 'urql'
import { authExchange } from '@urql/exchange-auth'
import { cacheExchange } from '@urql/exchange-graphcache'
import { persistedExchange } from '@urql/exchange-persisted'

import { useAuth } from './AuthProvider'
import { Auth } from './auth'
import { environment } from './environment'
import { schema } from './generated/schema'

const authApi = new Auth()

export const GqlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth, setAuth, isLoading, decodeToken } = useAuth()

  const client = useMemo(() => {
    const jwt = auth?.token
    return createClient({
      url: `${environment.API_URL}/user/graphql`,
      fetchOptions: () => ({
        headers: { Authorization: auth?.token ? `Bearer ${auth.token}` : '' },
      }),
      exchanges: [
        cacheExchange({
          schema: schema,
          keys: {
            Group: (data) => data.groupId as string,
            User: (data) => data.id as string,
            Company: (data) => data.id as string,
            Transaction: (data) => data.id as string,
            SharedTransaction: (data) => data.id as string,
            GroupStats: () => null, // Stats are query-specific, no caching needed
            CompanyStats: () => null, // Stats are query-specific, no caching needed
            MonthlyVolume: () => null, // Scoped by getMonthlyVolumes filter; do not normalize by month
            MintVolume: () => null, // Child of MonthlyVolume; volumes differ per filter scope
            SwapQuote: () => null, // Quotes are temporary and should not be cached
            DepositWallet: () => null, // No id field available, embed directly on parent entity
            // Paginated Connection types - cache by their paginated results
            UserTransactionsConnection: () => null, // Don't cache connections - cache individual nodes instead
            PageInfo: () => null, // Don't cache page info
          },
          updates: {
            Mutation: {
              // Swap management mutations
              createSwap: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },
              approveSwap: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getSwap')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },
              rejectSwap: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getSwaps')
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getSwaps: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getSwap')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },
              // createUserSwapNote: (_result, _args, cache) => {
              //   // Aggressive invalidation to force immediate UI updates
              //   cache.invalidate('Query', 'getSwap')
              //   cache.invalidate('Query', 'getUserSwapTransaction')

              //   // Invalidate all getSwap queries regardless of variables
              //   cache.invalidate({
              //     __typename: 'Query',
              //     getSwap: () => true,
              //   })

              //   // Invalidate all getUserSwapTransaction queries regardless of variables
              //   cache.invalidate({
              //     __typename: 'Query',
              //     getUserSwapTransaction: () => true,
              //   })

              //   // Invalidate specific fields on Query type
              //   cache.invalidate(
              //     {
              //       __typename: 'Query',
              //     },
              //     'getSwap',
              //   )
              //   cache.invalidate(
              //     {
              //       __typename: 'Query',
              //     },
              //     'getUserSwapTransaction',
              //   )
              // },
              // createUserDepositNote: (_result, _args, cache) => {
              //   // Aggressive invalidation to force immediate UI updates
              //   cache.invalidate('Query', 'getUserDepositTransaction')
              //   cache.invalidate('Query', 'getDepositTransaction')

              //   // Invalidate all getUserDepositTransaction queries regardless of variables
              //   cache.invalidate({
              //     __typename: 'Query',
              //     getUserDepositTransaction: () => true,
              //   })

              //   // Invalidate all getDepositTransaction queries regardless of variables
              //   cache.invalidate({
              //     __typename: 'Query',
              //     getDepositTransaction: () => true,
              //   })

              //   // Invalidate specific fields on Query type
              //   cache.invalidate(
              //     {
              //       __typename: 'Query',
              //     },
              //     'getUserDepositTransaction',
              //   )
              //   cache.invalidate(
              //     {
              //       __typename: 'Query',
              //     },
              //     'getDepositTransaction',
              //   )
              // },

              // Whitelisted address management mutations
              createWhitelistedAddress: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })
              },
              approveWhitelistedAddress: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })
              },
              rejectWhitelistedAddress: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })
              },
              updateWhitelistedAddress: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanyWhitelistedAddresses')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanyWhitelistedAddresses: () => true,
                })
              },

              // Transaction management mutations
              flagDeposit: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getDepositTransaction')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },
              flagSwap: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getSwap')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },
              approveDeposit: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getTransactions')
                cache.invalidate({
                  __typename: 'Query',
                  getTransactions: () => true,
                })
                cache.invalidate('Query', 'getDepositTransaction')
                cache.invalidate('Query', 'getCompanyStats')
                cache.invalidate('Query', 'getGroupStats')
              },

              // User management mutations
              updateMe: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getMe')
              },

              // Company/Group creation mutations (for completeness)
              createCompany: (_result, _args, cache) => {
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },

              // Authentication
              logout: (_result, _args, cache) => {
                // Clear entire cache on logout
                cache.invalidate('Query')
              },

              // Admin-related mutations that may be executed from user UI
              createUser: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for createUser in user UI')
                // Invalidate users-related queries if they exist in user UI
                cache.invalidate('Query', 'getUsers')
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },
              createGroupOwner: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for createGroupOwner in user UI')
                // Invalidate users-related queries if they exist in user UI
                cache.invalidate('Query', 'getUsers')
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },
              addUserToGroup: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for addUserToGroup in user UI')
                // Invalidate users and groups related queries
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getGroups')
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getGroups: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },
              updateUser: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for updateUser in user UI')
                // Invalidate users-related queries
                cache.invalidate('Query', 'getUsers')
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },
              assignUserToCompany: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for assignUserToCompany in user UI')
                // Invalidate company and user related queries
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getUsers')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },
              removeUserFromCompany: (_result, _args, cache) => {
                console.warn('🔧 Cache invalidation triggered for removeUserFromCompany in user UI')
                // Aggressive invalidation to force immediate UI updates
                cache.invalidate('Query', 'getCompanies')
                cache.invalidate('Query', 'getUsers')
                cache.invalidate('Query', 'getCompany')
                cache.invalidate('Query', 'getUser')
                cache.invalidate('Query', 'getMe')
                cache.invalidate({
                  __typename: 'Query',
                  getCompanies: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUsers: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getCompany: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getUser: () => true,
                })
                cache.invalidate({
                  __typename: 'Query',
                  getMe: () => true,
                })
                cache.invalidate('Query', 'getGroupStats')
              },

              // NOTE: Most admin mutations (createUser, createGroupOwner, etc.) don't affect
              // the user UI directly since users typically don't see admin management screens.
              // However, if user balances or company data changes, we invalidate those queries.
            },
          },
        }),
        authExchange(async (utils) => ({
          addAuthToOperation(operation) {
            // LOG THE API CALL
            // const definition = operation?.query?.definitions[0]
            // if (definition?.kind === 'OperationDefinition' && definition?.name?.kind === 'Name') {
            //   const { value } = definition.name
            //   console.warn('API Call: ', value)
            // }

            const currentJwt = auth?.token
            if (!currentJwt) return operation
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${currentJwt}`,
            })
          },
          willAuthError() {
            // Don't trigger auth errors while AuthProvider is still loading
            if (isLoading) {
              return false
            }

            let _isJwtStale: boolean = true
            if (jwt) {
              _isJwtStale = authApi.isJwtStale(jwt)
              if (_isJwtStale === true) {
                return true
              } else {
                return false
              }
            }
            return true
          },
          didAuthError(error) {
            const errorStr = error?.toString() || ''
            const errorMessage = error?.message || ''

            // Check for various JWT authentication errors
            const authErrors = [
              'Unauthorized',
              'JWT verification failed',
              'Token expired',
              'Invalid token',
              'Authentication failed',
              'Account has been deactivated',
            ]

            // Check error string and message for auth errors
            const hasAuthError = authErrors.some(
              (authError) => errorStr.includes(authError) || errorMessage.includes(authError),
            )

            // Check for 401 status code in various error formats
            const has401Status =
              errorStr.includes('401') ||
              errorMessage.includes('401') ||
              error?.response?.status === 401

            // Check for USER_INACTIVE error code specifically
            const isUserInactive =
              errorStr.includes('USER_INACTIVE') || errorMessage.includes('USER_INACTIVE')

            if (hasAuthError || has401Status || isUserInactive) {
              if (isUserInactive) {
                console.warn('User account deactivated:', {
                  error: errorStr,
                  message: errorMessage,
                })
              } else {
                console.warn('Authentication error detected:', {
                  error: errorStr,
                  message: errorMessage,
                })
              }
              return true
            } else {
              console.error('Non-auth error:', error)
              return false
            }
          },
          async refreshAuth() {
            try {
              if (auth?.refreshToken) {
                const res = await authApi.refresh(auth.refreshToken)
                const role = decodeToken(res.token)
                setAuth({ ...res, role: role.role, userId: role.userId })
              } else {
                setAuth(null)
              }
            } catch (e) {
              console.error('Failed to refresh token', e)
              setAuth(null)
            }
          },
        })),
        persistedExchange({
          enforcePersistedQueries: true,
          enableForMutation: true,
          generateHash: async (query: string) => {
            // Generate SHA256 hash of the query string
            const encoder = new TextEncoder()
            const data = encoder.encode(query)
            const hashBuffer = await crypto.subtle.digest('SHA-256', data)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
          },
        }),
        fetchExchange,
      ],
    })
  }, [auth?.refreshToken, auth?.token, setAuth, isLoading, decodeToken])
  return <GQLProvider value={client}>{children}</GQLProvider>
}
